import PropTypes from 'prop-types';

import { DOCUMENT_STATE } from '@entities/approval/constants';
import { formatDate } from '@shared/utils/formatDate';

import styles from './MobileDocumentInfo.module.scss';

/**
 * 모바일 문서 정보 컴포넌트
 * - 기안자, 기안일, 문서번호, 부서 등 기본 정보 표시
 */
function MobileDocumentInfo({ documentInfo, docData }) {
  // 날짜 포맷팅 (yyyyMMddHHmmss 형식의 문자열)
  const formattedDate = docData?.date ? formatDate(docData.date) : '-';

  // 실제 결재라인 상태를 기반으로 문서 상태 계산
  const actualDocumentState = calculateActualDocumentState(docData);

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <span className={styles.label}>기안자</span>
        <span className={styles.value}>{documentInfo?.drafterName || docData?.name || '-'}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>부서</span>
        <span className={styles.value}>{documentInfo?.department || docData?.team || '-'}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>기안일</span>
        <span className={styles.value}>{formattedDate}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>문서번호</span>
        <span className={styles.value}>{docData?.doc_number || '-'}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>문서상태</span>
        <span className={`${styles.value} ${styles.status} ${getStatusClass(actualDocumentState)}`}>
          {getDocumentStateText(actualDocumentState)}
        </span>
      </div>
    </div>
  );
}

/**
 * 실제 결재라인 상태를 기반으로 문서 상태 계산
 * - docData.state가 실제 결재라인 상태와 일치하지 않을 수 있으므로
 *   실제 결재라인의 상태를 확인하여 정확한 문서 상태를 반환
 * @param {Object} docData - 문서 데이터
 * @returns {number} 문서 상태 코드
 */
function calculateActualDocumentState(docData) {
  if (!docData) {
    return null;
  }

  // 결재라인 상태 수집 (sign_1은 기안자이므로 제외)
  const approvalLineStates = [];

  // 결재자 상태 (sign_2 ~ sign_5)
  for (let i = 2; i <= 5; i++) {
    const enumber = docData[`sign_${i}`];
    if (enumber) {
      const state = Number(docData[`sign_${i}_state`]);
      approvalLineStates.push(state);
    }
  }

  // 검토자 상태 (check_1 ~ check_4)
  for (let i = 1; i <= 4; i++) {
    const enumber = docData[`check_${i}`];
    if (enumber) {
      const state = Number(docData[`check_${i}_state`]);
      approvalLineStates.push(state);
    }
  }

  // 합의자 상태
  if (docData.agreement) {
    const state = Number(docData.agreement_state);
    approvalLineStates.push(state);
  }

  // 결재라인이 없으면 docData.state 반환
  if (approvalLineStates.length === 0) {
    return docData.state;
  }

  // 우선순위: 반려(4) > 수정요청(6) > 결재중(2) > 대기(1) > 완료(3)
  // 하나라도 반려가 있으면 반려
  if (approvalLineStates.some((state) => state === DOCUMENT_STATE.RETURN)) {
    return DOCUMENT_STATE.RETURN;
  }

  // 하나라도 수정요청이 있으면 수정요청
  if (approvalLineStates.some((state) => state === DOCUMENT_STATE.RETURNEDIT)) {
    return DOCUMENT_STATE.RETURNEDIT;
  }

  // 하나라도 결재중이 있으면 결재중
  if (approvalLineStates.some((state) => state === DOCUMENT_STATE.SIGNING)) {
    return DOCUMENT_STATE.SIGNING;
  }

  // 하나라도 대기가 있으면 결재대기
  if (approvalLineStates.some((state) => state === DOCUMENT_STATE.WAITSIGN)) {
    return DOCUMENT_STATE.WAITSIGN;
  }

  // 모두 완료(3)이거나 null이면 결재완료
  if (approvalLineStates.every((state) => state === DOCUMENT_STATE.SIGNED || !state)) {
    return DOCUMENT_STATE.SIGNED;
  }

  // 기본값: docData.state 반환
  return docData.state;
}

/**
 * 문서 상태 코드를 텍스트로 변환
 * - DOCUMENT_STATE 상수와 일치하도록 작성
 */
function getDocumentStateText(state) {
  switch (state) {
    case 0:
      return '진행중';
    case 1:
      return '결재대기';
    case 2:
      return '결재중';
    case 3:
      return '결재완료';
    case 4:
      return '반려';
    case 5:
      return '삭제';
    case 6:
      return '수정요청';
    default:
      return '-';
  }
}

/**
 * 문서 상태에 따른 스타일 클래스 반환
 * - PC와 동일한 컬러 적용: 결재완료(초록), 결재중(주황), 수정요청/반려(빨강)
 */
function getStatusClass(state) {
  switch (state) {
    case 2: // 결재중
      return 'statusPending';
    case 3: // 결재완료
      return 'statusCompleted';
    case 4: // 반려
    case 6: // 수정요청
      return 'statusRejected';
    default:
      return '';
  }
}

MobileDocumentInfo.propTypes = {
  documentInfo: PropTypes.shape({
    drafterName: PropTypes.string,
    department: PropTypes.string,
  }),
  docData: PropTypes.shape({
    name: PropTypes.string,
    team: PropTypes.string,
    date: PropTypes.string,
    doc_number: PropTypes.string,
    state: PropTypes.number,
  }),
};

MobileDocumentInfo.defaultProps = {
  documentInfo: {},
  docData: {},
};

export default MobileDocumentInfo;
